import * as yup from "yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import FormField from "@src/components/FormField/FormField";
import Input from "@src/components/Input/Input";
import "../styles/form.sass";
import Select from "@src/components/Select/Select";
import { ColorScheme, Preferences } from "@src/types/global";

interface IProps {
    userPreferences: Preferences;
    availableColorSchemes: ColorScheme[];
    submitFormFn: (data: Preferences) => void;
}

const UserPreferencesForm = ({
    userPreferences,
    availableColorSchemes,
    submitFormFn,
}: IProps) => {
    const schema = yup.object().shape({
        default_character_visibility: yup.boolean().required(),
        auto_grant_access_to_friends: yup.boolean().required(),
        measurement_unit: yup.string().oneOf(["metric", "imperial"]).required(),
        color_scheme: yup.string().required(),
        id: yup.string(),
    });

    interface FormValues extends yup.InferType<typeof schema> { }

    const {
        handleSubmit,
        formState: { errors },
        setValue,
        register,
        watch,
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: userPreferences,
    });

    /**
     * Transforms the data for submission
     * @param data
     */
    const onSubmit: SubmitHandler<FormValues> = (data) => {
        submitFormFn(data);
    };
    // Submits the form on change (when a box is checked)
    useEffect(() => {
        const subscription = watch(() => handleSubmit(onSubmit)());
        return () => subscription.unsubscribe();
    }, [handleSubmit, watch]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Input
                type="hidden"
                name="id"
                value={userPreferences ? userPreferences.id : ""}
                register={register}
            />
            <FormField
                hideLabel={true}
                name="default_character_visibility"
                errors={errors}
            >
                <Input
                    type="checkbox"
                    name="default_character_visibility"
                    label="Make character public by default"
                    register={register}
                />
            </FormField>
            <FormField
                name="auto_grant_access_to_friends"
                hideLabel={true}
                errors={errors}
            >
                <Input
                    type="checkbox"
                    label="Automatically grant character and world access to your friends"
                    name="auto_grant_access_to_friends"
                    register={register}
                />
            </FormField>
            <FormField
                name="measurement_unit"
                displayName="Prefered measurement unit"
                errors={errors}
            >
                <Select
                    name="measurement_unit"
                    options={[
                        { id: "imperial", name: "Imperial" },
                        { id: "metric", name: "Metric" },
                    ]}
                    onChange={(id: FormValues["measurement_unit"]) =>
                        setValue("measurement_unit", id)
                    }
                    defaultSelected={userPreferences.measurement_unit}
                />
            </FormField>
            <FormField
                name="color_scheme"
                displayName="Color scheme"
                errors={errors}
            >
                <Select
                    name="color_scheme"
                    options={availableColorSchemes.map((cs) => ({
                        name: cs.pretty_name,
                        id: cs.id,
                    }))}
                    onChange={(id) => setValue("color_scheme", id)}
                    defaultSelected={userPreferences.color_scheme}
                />
            </FormField>
        </form>
    );
};
export default UserPreferencesForm;
